import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiSearch } from "react-icons/fi";
import _ from "lodash";
import BackendLayout from "../../layouts/BackendLayout";
import { useDocumentTitle } from "../../hooks";
import { getSearchPageSettings } from "../../api/setting/pageApi";
import { search } from "../../api/search/searchApi";
import { API_SEARCH } from "../../constants/api";
import { USER_SETTING_CATEGORIES } from "../../constants/app";
import { SEARCH } from "../../constants/routes";
import {
  clearButtonProcessing,
  resetAlert,
  setButtonProcessing,
  setAlertError,
  setPageLoading,
  clearPageLoading,
  setPageSettings,
} from "../../store/actions";
import {
  FormGroup,
  PageTitle,
  SearchPanelMachineMaster,
  SearchPanelInspectionHistory,
  SearchType,
  PageAlert,
  Button,
  ButtonFilterSearch,
  RedirectAlert,
} from "../../components";
import getPageBreadcrumb from "../../services/BreadcrumbServices";
import { updateUserSetting } from "../../services/user-master/UserSettingServices";
import { getUrl, errorStatusHandle } from "../../utils";
import { allGroupIsEmpty } from "../../utils/select-utils";
import { getSelectOptionsSearchPage } from "../../services/SearchServices";
import { formatDateTime, toDatetimeStr } from "../../utils/datetime-utils";
import {SEARCH_PAGE} from "../../constants/pageName";
import useRedirectPage from "../../hooks/useRedirectPage";

const SearchPage = () => {
  useRedirectPage(SEARCH_PAGE);
  useDocumentTitle("詳細検索 - 点検王");
  const breadcrumb = getPageBreadcrumb(SEARCH);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [dataSearch, setDataSearch] = useState({
    type: "inspection",
    group_1: "",
    group_2: "",
    group_3: "",
    group_4: "",
    group_5: "",
    status: "active,inactive",
    start_datetime: "",
    end_datetime: "",
    machine_name: "",
    machine_id: "",
    template_id: "",
    inspection_number: null,
  });
  const [supportValues, setSupportValues] = useState({
    machine_tab: 0,
    date_ranger: "",
    disabled: true,
  });
  const [statusCheckbox, setStatusCheckbox] = useState({
    active: true,
    inactive: true,
  });
  const [pageLoaded, setPageLoaded] = useState(false);
  const [inputOptions, setInputOptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [machines, setMachines] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);

  const groupSettings = useSelector(
    (state) => state.app_settings.machine_group
  );
  const setSearchPageSettings = async (e) => {
    dispatch(setPageLoading());
    const pageSettings = await getSearchPageSettings();
    setInputOptions(pageSettings.input_options);
    setTemplates(
      getSelectOptionsSearchPage(pageSettings.input_options.templates)
    );
    setMachines(
      getSelectOptionsSearchPage(pageSettings.input_options.machines)
    );
    setInspectionFrequencyOptions(pageSettings.input_options.inspection_number);
    if (pageSettings.user_setting?.setting) {
      handleSetDataSearch(
        pageSettings.user_setting.setting,
        pageSettings.input_options
      );
    }
    dispatch(setPageSettings(pageSettings));
    dispatch(clearPageLoading());
    setPageLoaded(true);
  };

  useEffect(() => {
    setSearchPageSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetDataSearch = (userSettings, dataOptions) => {
    const result = {};
    Object.keys(userSettings).forEach((key) => {
      let optionKey = "";
      if (key.startsWith("group_")) {
        optionKey = key;
      }
      if (key === "template_id" || key === "machine_id") {
        optionKey = key.replace("_id", "s");
      }
      if (optionKey !== "" && dataOptions[optionKey]) {
        let isValidGroupValue = false;
        if (Array.isArray(userSettings[key])) {
          isValidGroupValue = dataOptions[optionKey].some((option) =>
            userSettings[key].includes(option.id)
          );
        } else {
          isValidGroupValue = dataOptions[optionKey].some(
            (option) => option.id === parseInt(userSettings[key])
          );
        }
        result[key] = userSettings[key];
        if (!isValidGroupValue) {
          result[key] = "";
        }
      }
    });
    const initStatus = userSettings?.status || "active,inactive";
    result["type"] = userSettings?.type || "inspection";
    result["status"] = initStatus;
    result["machine_name"] = userSettings?.machine_name || "";
    result["inspection_number"] =
      parseInt(userSettings?.inspection_number) || null;
    const machineTab = userSettings?.machine_tab || 0;

    if (initStatus === "active") {
      setStatusCheckbox({ ...statusCheckbox, inactive: false });
    }
    if (initStatus === "inactive") {
      setStatusCheckbox({ ...statusCheckbox, active: false });
    }
    setSupportValues({ ...supportValues, machine_tab: machineTab });
    getOptionsMachineAndTemplate(userSettings, dataOptions);
    setDataSearch((prevDataSearch) => ({
      ...prevDataSearch,
      ...result,
    }));
    return result;
  };

  const getOptionsMachineAndTemplate = (newSearchData, inputOptions) => {
    const templateOptions = inputOptions["templates"] || [];
    const machineOptions = inputOptions["machines"] || [];
    if (allGroupIsEmpty(newSearchData)) {
      setTemplates(getSelectOptionsSearchPage(templateOptions));
      setMachines(getSelectOptionsSearchPage(machineOptions));
    } else {
      getTemplateOptions(templateOptions, newSearchData);
      getMachineOptions(machineOptions, newSearchData);
    }
  };

  const getTemplateOptions = (templateOptions, newSearchData) => {
    const filteredTemplates = templateOptions.filter((template) => {
      return Object.keys(newSearchData).every((key) => {
        if (
          key.startsWith("group_") &&
          newSearchData[key] !== "" &&
          template[key] !== null
        ) {
          if (Array.isArray(newSearchData[key])) {
            return newSearchData[key].includes(template[key]);
          } else {
            return newSearchData[key] === template[key];
          }
        }
        return true;
      });
    });
    setTemplates(getSelectOptionsSearchPage(filteredTemplates));
  };

  const getMachineOptions = (machineOptions, newSearchData) => {
    const filteredMachines = machineOptions.filter((machine) => {
      return Object.keys(newSearchData).every((key) => {
        if (
          key.startsWith("group_") &&
          newSearchData[key] !== "" &&
          machine["group_relation"][`${key}_id`] !== null
        ) {
          if (Array.isArray(newSearchData[key])) {
            return newSearchData[key].includes(
              machine["group_relation"][`${key}_id`]
            );
          } else {
            return (
              newSearchData[key] === machine["group_relation"][`${key}_id`]
            );
          }
        }
        return true;
      });
    });
    setMachines(getSelectOptionsSearchPage(filteredMachines));
  };

  const setInspectionFrequencyOptions = (maxValue) => {
    const options = Array.from({ length: maxValue }, (_, index) => ({
      value: index + 1,
      label: (index + 1).toString(),
    }));
    const frequencyoptions = [{ value: null, label: "[ALL]" }, ...options];
    setFrequencyOptions(frequencyoptions);
  };

  const handleSelectChange = async (key, selectedValue) => {
    let newSearchData = { ...dataSearch };
    newSearchData[key] = selectedValue;
    if (key.startsWith("group_")) {
      const groupNumber = parseInt(key.split("_")[1], 10);
      for (let i = groupNumber + 1; i <= 5; i++) {
        newSearchData[`group_${i}`] = "";
      }
      newSearchData["machine_id"] = "";
      newSearchData["template_id"] = "";
    }
    setDataSearch((prevDataSearch) => ({
      ...prevDataSearch,
      ...newSearchData,
    }));
    getOptionsMachineAndTemplate(newSearchData, inputOptions);
    setErrors({});
  };

  useEffect(() => {
    if (window.location.search.length === 0) {
      return;
    }
    search(`${API_SEARCH}${window.location.search}`)
      .then((response) => {})
      .catch((error) => {
        errorStatusHandle(error);
        if (error.status === 400) {
          let response = error.data;
          dispatch(setAlertError(response.error_message));
          if (!_.isEmpty(response.errors)) {
            setErrors(response.errors);
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitSearch = (e, buttonId) => {
    let form = { ...dataSearch };
    if (supportValues.machine_tab === 0) {
      delete form.machine_id;
    }
    if (supportValues.machine_tab === 1) {
      delete form.machine_name;
    }
    form = checkDatetimeInvalie(form);
    dispatch(setButtonProcessing(buttonId));
    dispatch(resetAlert());
    setErrors({});
    search(getUrl(form, API_SEARCH))
      .then((response) => {
        updateSettings();
        window.location.href = `/${response.data.search_result}`;
      })
      .catch((error) => {
        errorStatusHandle(error);
        if (error.status === 400) {
          let response = error.data;
          dispatch(setAlertError(response.error_message));
          if (!_.isEmpty(response.errors)) {
            setErrors(response.errors);
          }
        }
      })
      .finally(() => {
        dispatch(clearButtonProcessing(buttonId));
      });
  };

  const checkDatetimeInvalie = (form) => {
    const start = form["start_datetime"];
    const end = form["end_datetime"];
    if ((start === "" || start === null) && (end === "" || end === null)) {
      return form;
    }
    if (start !== "" && start !== null && (end === "" || end === null)) {
      form["end_datetime"] = formatDateTime(new Date());
    }
    if (start instanceof Date && !isNaN(start)) {
      form["start_datetime"] = toDatetimeStr(start);
    }
    if (end instanceof Date && !isNaN(end)) {
      form["end_datetime"] = toDatetimeStr(end);
    }
    return form;
  };

  const updateSettings = async () => {
    const formUserSettings = {
      group_1: dataSearch.group_1,
      group_2: dataSearch.group_2,
      group_3: dataSearch.group_3,
      group_4: dataSearch.group_4,
      group_5: dataSearch.group_5,
      type: dataSearch.type,
      status: dataSearch.status,
      template_id: dataSearch.template_id,
      machine_tab: supportValues.machine_tab,
      inspection_number: dataSearch.inspection_number,
    };
    if (supportValues.machine_tab === 1) {
      formUserSettings["machine_id"] = dataSearch.machine_id;
    }
    if (supportValues.machine_tab === 0) {
      formUserSettings["machine_name"] = dataSearch.machine_name;
    }

    try {
      await updateUserSetting(formUserSettings, USER_SETTING_CATEGORIES.SEARCH);
    } catch (e) {
      errorStatusHandle(e);
    }
  };

  return (
    <BackendLayout breadcrumb={breadcrumb}>
      <PageTitle title="詳細検索" />
      <RedirectAlert />
      <PageAlert />
      <div className="pr-6 w-fit">
        <div className="2xl:px-12 xl:px-10 lg:px-8 px-6 xl:py-6 py-5 rounded-lg border border-tenken-gray-300 bg-white inline-block mb-15">
          <div className="xl:mt-[32px] mt-[24px] space-y-[24px]">
            <SearchType
              errors={errors}
              dataSearch={dataSearch}
              setDataSearch={setDataSearch}
            />
            {dataSearch.type === "machine" ? (
              <SearchPanelMachineMaster
                groupSettings={groupSettings}
                errors={errors}
                dataSearch={dataSearch}
                setDataSearch={setDataSearch}
                inputOptions={inputOptions}
                statusCheckbox={statusCheckbox}
                setStatusCheckbox={setStatusCheckbox}
                handleSelectChange={handleSelectChange}
              />
            ) : (
              <SearchPanelInspectionHistory
                groupSettings={groupSettings}
                errors={errors}
                dataSearch={dataSearch}
                setDataSearch={setDataSearch}
                inputOptions={inputOptions}
                machineOptions={machines}
                templateOptions={templates}
                pageLoaded={pageLoaded}
                supportValues={supportValues}
                setSupportValues={setSupportValues}
                handleSelectChange={handleSelectChange}
                frequencyOptions={frequencyOptions}
              />
            )}
          </div>
          <div className="lg:mt-12 mt-10 flex justify-center">
            <ButtonFilterSearch
              text="検索実行"
              onClick={(e) => handleSubmitSearch(e, "search-button")}
              className="lg:w-[200px] w-[180px]"
            />
          </div>
        </div>
      </div>
    </BackendLayout>
  );
};

export default SearchPage;
